import React from 'react';
import { FileText, Database, ClipboardList, Settings, ArrowRight, CheckCircle, AlertCircle, BarChart, Globe, Warehouse, Users } from 'lucide-react';

const Services = () => {
  const services = [
    {
      id: "managed-services",
      Icon: Users,
      title: "Managed Services",
      description: "Rely on our industry experts to manage the complex and time-consuming parts of your rail operations — so you can focus on what matters most. Our managed services team supports everything from fleet utilization to regulatory compliance, lease administration, maintenance, and more.",
      features: [
        "Proactive fleet management and railcar tracking",
        "Lease audits, mileage credit collection, and administration",
        "Maintenance program oversight and invoice auditing",
        "Compliance with Umler®, LOA, Railinc, and TCID requirements",
        "Expert advisory across engineering, hazmat, and training"
      ],
      whyItMatters: "Free up your team to focus on core business activities while ensuring optimal fleet performance and compliance.",
      ctaText: "Learn about our managed services",
      image: "/images/Railcar 1.jpg"
    },
    {
      id: "yard-management",
      Icon: Warehouse,
      title: "Yard Management System",
      description: "Optimize your rail yard operations with our intelligent yard management system. Get real-time visibility into yard activities, automate switch lists, and improve throughput with smart planning tools.",
      features: [
        "Real-time yard visibility and tracking",
        "Automated switch list generation",
        "Intelligent car placement optimization",
        "Integration with terminal operating systems"
      ],
      whyItMatters: "Efficient yard management can reduce dwell time by up to 25% and significantly improve operational efficiency.",
      ctaText: "Optimize your yard operations",
      image: "/images/Railcar 5.jpg"
    },
    {
      id: "fleet-management",
      Icon: Globe,
      title: "Fleet Management Software",
      description: "Take control of your rail fleet with our comprehensive management platform. Track equipment, monitor utilization, and optimize your operations all in one place.",
      features: [
        "Real-time fleet tracking and monitoring",
        "Equipment utilization analytics",
        "Maintenance scheduling and tracking",
        "Custom reporting and dashboards"
      ],
      whyItMatters: "Maximize your fleet's efficiency and reduce operational costs with data-driven insights.",
      ctaText: "Optimize your fleet management",
      image: "/images/Railway 5.jpg"
    },
    {
      id: "railinc-integration",
      Icon: Database,
      title: "Railinc Integration Services",
      description: "Seamlessly connect with Railinc's systems and simplify your data exchange processes. Our integration solutions ensure smooth communication between your systems and industry platforms.",
      features: [
        "Automated data synchronization",
        "EDI message handling",
        "Error monitoring and alerts",
        "Custom integration solutions"
      ],
      whyItMatters: "Eliminate manual data entry and ensure accurate, timely information exchange with Class I railroads.",
      ctaText: "Streamline your Railinc integration",
      image: "/images/Railway 2.jpg"
    },
    {
      id: "car-hire",
      Icon: BarChart,
      title: "Car Hire Management",
      description: "Simplify your car hire processes with our automated management system. Track expenses, validate charges, and optimize your fleet utilization.",
      features: [
        "Automated rate management",
        "Charge validation and reconciliation",
        "Exception handling and dispute resolution",
        "Historical data analysis"
      ],
      whyItMatters: "Reduce car hire costs and improve accountability with automated tracking and validation.",
      ctaText: "Enhance your car hire management",
      image: "/images/Railcar 3.jpg"
    },
    {
      id: "reporting-analytics",
      Icon: Settings,
      title: "Reporting & Analytics",
      description: "Transform your data into actionable insights with our comprehensive reporting and analytics tools. Make informed decisions with real-time visibility into your operations.",
      features: [
        "Custom report builder",
        "Interactive dashboards",
        "Performance metrics tracking",
        "Trend analysis and forecasting"
      ],
      whyItMatters: "Make data-driven decisions with clear, actionable insights into your operations.",
      ctaText: "Unlock your data potential",
      image: "/images/Railway 3.jpg"
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/Railway 2.jpg"
            alt="Rail services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gray-900/80"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold text-white mb-6">
              Rail Management Solutions
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Comprehensive software solutions designed specifically for the rail industry. From fleet management to analytics, we help you optimize every aspect of your operations.
            </p>
          </div>
        </div>
      </section>

      {/* Detailed Service Sections */}
      {services.map((service, index) => (
        <section 
          key={service.id} 
          id={service.id}
          className={`py-24 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center mb-6">
                  <service.Icon className="h-12 w-12 text-blue-600 mr-4" />
                  <h2 className="text-3xl font-bold text-gray-900">{service.title}</h2>
                </div>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  {service.description}
                </p>
                <div className="space-y-6 mb-8">
                  <h3 className="text-xl font-semibold text-gray-900">Key Features:</h3>
                  <div className="space-y-4">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start">
                        <CheckCircle className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                        <span className="text-lg text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Why it matters:</h3>
                  <div className="flex items-start">
                    <AlertCircle className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-lg text-gray-700">{service.whyItMatters}</p>
                  </div>
                </div>
                <a 
                  href="/contact" 
                  className="inline-flex items-center bg-blue-600 text-white font-bold py-4 px-8 rounded-md hover:bg-blue-700 transition-colors text-lg"
                >
                  {service.ctaText}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </div>
              <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/Railway 2.jpg"
            alt="Rail operations"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900/90"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Transform Your Rail Operations?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how our solutions can optimize your rail fleet management
          </p>
          <a 
            href="/contact" 
            className="inline-flex items-center justify-center bg-white text-blue-900 font-bold py-4 px-8 rounded-md hover:bg-blue-50 transition-colors text-lg"
          >
            Schedule a Demo
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Services;